"use client";

import Link from "next/link";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PlanGrid } from "@/components/billing/components/PlanGrid";
import { useOrgOptional } from "@/components/orgs/OrgContext";
import {
  isCreditsRestriction,
  type PlanRestrictionError,
} from "@/lib/planRestrictionHandler";

interface UpgradePromptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feature?: string;
  currentPlan?: string;
  /** Org to subscribe; omitted = the org in scope, else personal */
  orgSlug?: string;
  /**
   * The restriction that opened the dialog. When credits get past it
   * (a scan the balance can't cover, prompts over the plan's cap), the
   * dialog leads with the server's explanation and "Buy credits".
   */
  restriction?: PlanRestrictionError;
}

export function UpgradePromptDialog({
  open,
  onOpenChange,
  feature,
  currentPlan,
  orgSlug,
  restriction,
}: UpgradePromptDialogProps) {
  const orgCtx = useOrgOptional();
  const slug = orgSlug ?? orgCtx?.orgSlug;
  const credits = restriction && isCreditsRestriction(restriction);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] gap-0 p-0">
        {credits ? (
          <DialogHeader className="space-y-3 border-b p-6 text-left">
            <DialogTitle>
              {restriction.errorType === "INSUFFICIENT_CREDITS"
                ? "Not enough credits"
                : "Over your plan's prompt limit"}
            </DialogTitle>
            <DialogDescription>{restriction.message}</DialogDescription>
            {slug ? (
              <div>
                <Button asChild size="sm">
                  <Link
                    href={`/o/${slug}/settings/billing`}
                    onClick={() => onOpenChange(false)}
                  >
                    Buy credits
                  </Link>
                </Button>
              </div>
            ) : null}
          </DialogHeader>
        ) : (
          <DialogHeader>
            <DialogTitle className="sr-only">Upgrade your plan</DialogTitle>
          </DialogHeader>
        )}
        <div className="p-6">
          {credits ? (
            <p className="pb-4 text-sm text-muted-foreground">
              Or upgrade for a bigger allowance.
            </p>
          ) : null}
          <PlanGrid
            feature={credits ? undefined : feature}
            currentPlan={currentPlan}
            orgSlug={slug}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
